import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, Phone, MapPin, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/landing/Navbar";
import FooterSection from "@/components/landing/FooterSection";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 13) {
    // 5527992266635 -> (27) 9 9226-6635
    return `(${digits.slice(2, 4)}) ${digits[4]} ${digits.slice(5, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits[2]} ${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const Contato = () => {
  const { config } = usePlatformConfig();
  const [sending, setSending] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast.success("Mensagem enviada com sucesso! Responderemos em até 24h.");
      (e.target as HTMLFormElement).reset();
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar ao início
          </Link>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                Contato
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Fale com a gente</h1>
              <p className="text-lg text-muted-foreground mb-10">
                Tem dúvidas, sugestões ou precisa de ajuda? Nosso time está pronto para te atender.
              </p>

              <div className="space-y-6">
                {[
                  config.contact_email && { icon: Mail, label: "E-mail", value: config.contact_email, href: `mailto:${config.contact_email}` },
                  config.contact_phone && { icon: Phone, label: "Telefone", value: formatPhone(config.contact_phone), href: `tel:+${config.contact_phone.replace(/\D/g, "")}` },
                  config.contact_whatsapp && { icon: MessageSquare, label: "WhatsApp", value: formatPhone(config.contact_whatsapp), href: `https://wa.me/${config.contact_whatsapp.replace(/\D/g, "")}` },
                  config.contact_address && { icon: MapPin, label: "Endereço", value: config.contact_address, href: "#" },
                ].filter(Boolean).map((c: any) => (
                  <a key={c.label} href={c.href} className="flex items-center gap-4 group" target={c.href.startsWith("http") ? "_blank" : undefined}>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <c.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">{c.label}</div>
                      <div className="font-medium group-hover:text-primary transition-colors">{c.value}</div>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-gradient-card border border-border space-y-5">
                <h2 className="text-xl font-bold mb-2">Envie sua mensagem</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">Nome</label>
                    <Input required placeholder="Seu nome" className="bg-muted border-border" />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">E-mail</label>
                    <Input required type="email" placeholder="seu@email.com" className="bg-muted border-border" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Assunto</label>
                  <Input required placeholder="Sobre o que deseja falar?" className="bg-muted border-border" />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1.5 block">Mensagem</label>
                  <Textarea required rows={5} placeholder="Descreva sua dúvida, sugestão ou problema..." className="bg-muted border-border resize-none" />
                </div>
                <Button type="submit" disabled={sending} className="w-full h-12 bg-gradient-primary text-primary-foreground font-semibold">
                  {sending ? "Enviando..." : <><Send className="mr-2 h-4 w-4" /> Enviar Mensagem</>}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default Contato;
